import "./MatchPatient.styles.scss";
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PatientsDropdown, GroupNameTag, Button } from "components";
import {
  useGetGroupUsers,
  useAssignSubmissionToPatient,
  useCurrentUser,
  useGetPractices,
  useDeletePracticeSubmission,
  usePublicSubmission,
  useAssignPatientToTherapistFromUnassigned,
  useGetOrganizationUsers,
} from "utils/hooks";
import useOrganizationSettings, {
  getPatientWord,
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";
import DeleteModal from "pages/AppPages/Shared/DeleteModal";
import { User, AssignmentSubmission } from "types";
import { getPatientNameEmail } from "pages/AppPages/TherapistPages/Inbox/utils/helpers";
import { GROUP_USER_ROLES } from "utils/constants";

type MatchPatientProps = {
  submission: AssignmentSubmission | undefined;
  onClose: (() => void) | undefined;
};

const MatchPatient = ({ submission, onClose }: MatchPatientProps) => {
  const { t } = useTranslation(["common"]);
  const { settings: orgSettings } = useOrganizationSettings();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [edit, setEdit] = useState(submission?.userId ? false : true);
  const [showTherapistList, setShowTherapistList] = useState(false);
  const { groupUsers } = useGetGroupUsers({
    groupId: submission?.groupId || null,
  });
  const { assignSubmissionToPatient, loading } = useAssignSubmissionToPatient({
    submissionId: submission?.uuid,
  });
  const { deletePracticeSubmission, deleting } = useDeletePracticeSubmission({
    submissionId: submission?.uuid,
  });
  const { user } = useCurrentUser();
  const { practices } = useGetPractices();
  const { organizationUsers } = useGetOrganizationUsers({
    organizationId: user?.activeOrganizationId || null,
  });
  const { addPatientFromPublicSubm, addPatientRunning } = usePublicSubmission();
  const { assignPatientToTherapist, assigning } =
    useAssignPatientToTherapistFromUnassigned({
      submissionId: submission?.uuid,
    });
  const groupPatients = groupUsers?.filter(
    (user: User) => user.role === GROUP_USER_ROLES.MEMBER
  );
  const isPracticeOwner = practices[0]?.practiceOwnerId
    ? user?.id === practices[0]?.practiceOwnerId
    : true;

  useEffect(() => {
    if (!assigning) setShowTherapistList(false);
  }, [assigning]);

  useEffect(() => setEdit(!Boolean(submission?.userId)), [submission]);

  const handleAssignSubmissionToPatient = (e: any) => {
    if (!e?.value) {
      toast.warn(
        t("patients.choose_patient_warn", {
          patientWord: getPatientWord(orgSettings),
        })
      );
      return;
    }
    if (loading) return;
    assignSubmissionToPatient({ patientId: e?.value });
  };

  const handleAssignToTherapist = (e: any) => {
    if (!e?.value) {
      toast.warn(t("calls_to_action.select_therapist"));
      return;
    }
    assignPatientToTherapist({ therapistId: e.value });
  };

  const handleDeleteSubmission = () => {
    setOpenDeleteModal(false);
    deletePracticeSubmission();
    onClose && onClose();
  };

  const handleAddPatient = async () => {
    if (!submission) return;
    addPatientFromPublicSubm(submission?.uuid);
  };

  const patientNameEmail = useMemo(() => {
    if (!submission) return null;
    const nameEmail = getPatientNameEmail(submission.userId, groupPatients);
    return (
      nameEmail || { name: submission?.userName, email: submission?.userEmail }
    );
  }, [groupPatients, submission]);

  if (!isPracticeOwner) return null;
  return (
    <div
      className={`match-patient ${
        submission?.isWideData ? "match-patient__flex-space" : ""
      }`}
    >
      {!submission?.userId || edit ? (
        <div className="match-patient__input">
          <PatientsDropdown
            label={t("inbox.match_patient_label", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            placeholder={t("calls_to_action.choose_label")}
            patientsList={groupPatients}
            onChange={handleAssignSubmissionToPatient}
          />
          {submission?.groupName && (
            <GroupNameTag groupName={submission.groupName} />
          )}
        </div>
      ) : (
        <div className="match-patient__name-email">
          {patientNameEmail?.name && (
            <div className="name">{patientNameEmail.name}</div>
          )}
          <div className="email">{patientNameEmail?.email}</div>
        </div>
      )}
      <div className="match-patient__buttons">
        <Button
          value={t("common_labels.edit")}
          name="match-patient-edit-btn"
          extraClass="assignment-card_btn"
          icon="EditIconBold"
          isLoading={loading}
          onClick={() => setEdit(!edit)}
        />
        {submission?.userId && (
          <div className="match-patient__button">
            <Button
              value={t("calls_to_action.assign")}
              name="match-patient-assign-btn"
              extraClass="assignment-card_btn"
              icon="UserChecked"
              isLoading={assigning}
              onClick={() => setShowTherapistList(!showTherapistList)}
            />
            {showTherapistList && (
              <div className="match-patient__therapists">
                <PatientsDropdown
                  placeholder={t("calls_to_action.choose_label")}
                  patientsList={organizationUsers}
                  menuIsOpen={true}
                  onChange={handleAssignToTherapist}
                />
              </div>
            )}
          </div>
        )}
        {!submission?.userId && (
          <Button
            value={t("patients.add_patient_button", {
              patientWord: getPatientWord(orgSettings),
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
            name="match-patient-add-btn"
            extraClass="assignment-card_btn"
            icon="AddPatientBold"
            isLoading={addPatientRunning}
            onClick={handleAddPatient}
          />
        )}
        <Button
          value={t("common_labels.delete")}
          name="match-patient-delete-btn"
          extraClass="assignment-card_btn"
          icon="TrashBold"
          isLoading={deleting}
          onClick={() => setOpenDeleteModal(true)}
        />
      </div>
      <DeleteModal
        isOpen={openDeleteModal}
        loading={deleting}
        fullScreen={true}
        onCancel={() => setOpenDeleteModal(false)}
        onConfirm={handleDeleteSubmission}
      />
    </div>
  );
};

export default MatchPatient;
